import type { ClubRole } from "@rivaled/shared";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ExternalLink,
  HelpCircle,
  Loader2,
  Plus,
  Save,
  Settings,
  Shield,
  Sliders,
  Swords,
  Trash2,
  Trophy,
  Users,
  Workflow,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { FeatureGate } from "../../../../components/FeatureGate";
import type { NavItem, SectionId } from "../../../../components/queue-settings";
import {
  CreateQueueForm,
  DangerSection,
  GeneralSection,
  MatchmakingSection,
  MatchSection,
  RanksSection,
  RestrictionsSection,
  sectionDocs,
  sectionIds,
  sectionTitles,
  TeamSection,
  useQueueSettingsForm,
  type QueueData,
} from "../../../../components/queue-settings";
import {
  Breadcrumbs,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  Modal,
  ModalFooter,
  SettingsSection,
} from "../../../../components/ui";
import { usePlatformRole } from "../../../../hooks/usePlatformRole";
import { canClubRolePermission } from "../../../../lib/permissions";
import { trpc, type RouterOutputs } from "../../../../lib/trpc";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

type QueueListItem = {
  id: string;
  slug: string;
  name: string;
};

export const Route = createFileRoute("/app/clubs/$slug/queue-settings")({
  validateSearch: (search: Record<string, unknown>): { queue?: string; section: SectionId } => {
    const s = search.section;
    const section =
      typeof s === "string" && (sectionIds as readonly string[]).includes(s)
        ? (s as SectionId)
        : "general";
    const queue = typeof search.queue === "string" ? search.queue : undefined;
    return { queue, section };
  },
  component: QueueSettingsPage,
});

// ---------------------------------------------------------------------------
// Nav config
// ---------------------------------------------------------------------------

const navItems: NavItem[] = [
  { id: "general", label: "General", icon: Settings },
  { id: "team", label: "Team Config", icon: Users },
  { id: "restrictions", label: "Restrictions", icon: Shield },
  { id: "match", label: "Match Settings", icon: Swords },
  { id: "matchmaking", label: "Matchmaking", icon: Sliders },
  { id: "ranks", label: "Ranks", icon: Trophy },
  { id: "danger", label: "Delete", icon: Trash2, danger: true },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function QueueSettingsPage() {
  const { slug } = Route.useParams();
  const { queue: searchQueueSlug, section } = Route.useSearch();
  const navigate = useNavigate();
  const { isLoading: isPlatformRoleLoading, isModerator: isPlatformModerator } = usePlatformRole();

  const { data: myClubs, isLoading: myClubsLoading } = trpc.club.getMyClubs.useQuery();
  const { data: club, isLoading: clubLoading } = trpc.club.get.useQuery({ slug });
  const membership = myClubs?.find((c) => c.slug === slug);
  const clubId = membership?.id ?? club?.id;
  const isAdmin =
    canClubRolePermission(membership?.role as ClubRole | undefined, {
      resource: "queue",
      actions: ["update"],
    }) || isPlatformModerator;

  const { data: queues, isLoading: queuesLoading } = trpc.queue.list.useQuery(
    { clubId: clubId ?? "" },
    { enabled: !!clubId },
  );

  const [selectedQueueSlug, setSelectedQueueSlug] = useState<string | null>(
    searchQueueSlug ?? null,
  );
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Resolve slug → ID for fetching
  const selectedQueueId =
    (queues as QueueListItem[] | undefined)?.find((q) => q.slug === selectedQueueSlug)?.id ?? null;

  // Fetch selected queue data — keep previous data to avoid flicker on tab switch
  const { data: queue, isLoading: queueLoading } = trpc.queue.get.useQuery(
    { queueId: selectedQueueId! },
    { enabled: !!selectedQueueId, placeholderData: (prev) => prev },
  );

  // Form hook for the selected queue
  const form = useQueueSettingsForm(selectedQueueId ?? "", clubId);

  // Populate form when queue data loads
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- populateForm is stable via useCallback
  useEffect(() => {
    if (queue) {
      form.populateForm(queue as QueueData);
    }
  }, [queue]);

  // Auto-select first queue if none specified, or reset if selected queue no longer exists
  useEffect(() => {
    if (!queues?.length) return;
    if (!selectedQueueSlug || !queues.some((q) => q.slug === selectedQueueSlug)) {
      setSelectedQueueSlug(queues[0].slug);
    }
  }, [queues, selectedQueueSlug]);

  // Sync URL when queue changes
  useEffect(() => {
    if (selectedQueueSlug) {
      void navigate({
        to: "/app/clubs/$slug/queue-settings",
        params: { slug },
        search: { queue: selectedQueueSlug, section },
        replace: true,
      });
    }
  }, [selectedQueueSlug, section, slug, navigate]);

  const selectedQueue = queues?.find((q) => q.slug === selectedQueueSlug);

  const setSection = (id: SectionId) => {
    void navigate({
      to: "/app/clubs/$slug/queue-settings",
      params: { slug },
      search: { queue: selectedQueueSlug ?? undefined, section: id },
      replace: true,
    });
  };

  // Access check
  if (
    !myClubsLoading &&
    !clubLoading &&
    !queuesLoading &&
    !isPlatformRoleLoading &&
    (!isAdmin || !clubId)
  ) {
    void navigate({ to: "/app/clubs/$slug", params: { slug } });
    return null;
  }

  if (myClubsLoading || clubLoading || queuesLoading || isPlatformRoleLoading) {
    return (
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        <div className="h-8 w-48 rounded-lg bg-surface-700/40 animate-pulse" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 rounded-xl bg-surface-800/50 animate-pulse" />
        ))}
      </div>
    );
  }

  const selectQueue = (queueSlug: string) => {
    setSelectedQueueSlug(queueSlug);
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="space-y-3">
        <Breadcrumbs
          items={[
            {
              label: membership?.name ?? club?.name ?? "Club",
              to: "/app/clubs/$slug",
              params: { slug },
            },
            { label: "Queue Settings" },
          ]}
        />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Queue Settings</h1>
            <p className="text-sm text-surface-400">
              Configure matchmaking, teams, restrictions, and ranks for your queues.
            </p>
          </div>
          {selectedQueue && queue && (
            <Button
              variant="primary"
              size="md"
              leftIcon={
                form.saving ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )
              }
              disabled={form.saving || !form.name.trim()}
              onClick={() => form.handleSave(queue)}
            >
              {form.saving ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      {selectedQueue && queue ? (
        <QueueSettingsContent
          queue={queue}
          form={form}
          clubId={clubId!}
          slug={slug}
          section={section}
          setSection={setSection}
          queues={queues ?? []}
          selectedQueueSlug={selectedQueueSlug}
          selectQueue={selectQueue}
          onCreateQueue={() => setShowCreateModal(true)}
        />
      ) : selectedQueueId && queueLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 rounded-xl bg-surface-800/50 animate-pulse" />
          ))}
        </div>
      ) : queues?.length === 0 ? (
        <div className="rounded-xl bg-surface-800/50 border border-surface-700 p-8 text-center space-y-3">
          <p className="text-sm text-surface-400">No queues yet. Create one to get started.</p>
          <Button
            variant="primary"
            leftIcon={<Plus className="size-4" />}
            onClick={() => setShowCreateModal(true)}
          >
            Create Queue
          </Button>
        </div>
      ) : null}

      {/* Create Queue Modal */}
      {clubId && (
        <Modal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          className="!max-w-md"
        >
          <div className="px-6 pt-6 pb-1">
            <h2 className="text-xl font-bold text-white">Create Queue</h2>
            <p className="text-sm text-surface-400 mt-1">Give your queue a name to get started</p>
          </div>
          <div className="px-6 py-4">
            <CreateQueueForm
              clubId={clubId}
              slug={slug}
              onCreated={(newSlug) => {
                setShowCreateModal(false);
                selectQueue(newSlug);
              }}
            />
          </div>
        </Modal>
      )}

      {/* Delete confirmation modal */}
      {queue && (
        <Modal
          open={form.showDeleteModal}
          onClose={() => form.setShowDeleteModal(false)}
          className="!max-w-sm"
        >
          <div className="px-6 pt-6 pb-2">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-error-500/10 border border-error-500/20">
                <AlertTriangle className="size-5 text-error-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Delete Queue</h2>
            </div>
            <p className="text-sm text-surface-400">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-white">{queue.name}</span>? This will remove all
              queue data, stats, and match history associated with it.
            </p>
          </div>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => form.setShowDeleteModal(false)}
              disabled={form.deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              leftIcon={
                form.deleteMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Trash2 className="size-4" />
                )
              }
              disabled={form.deleteMutation.isPending}
              onClick={() =>
                form.handleDelete(slug, () => {
                  void navigate({
                    to: "/app/clubs/$slug/queue-settings",
                    params: { slug },
                    search: { section: "general" },
                  });
                  window.location.reload();
                })
              }
              className="!bg-error-500 hover:!bg-error-600"
            >
              {form.deleteMutation.isPending ? "Deleting..." : "Delete Queue"}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Queue settings content (sections + sidebar)
// ---------------------------------------------------------------------------

function QueueSettingsContent({
  queue,
  form,
  clubId,
  slug,
  section,
  setSection,
  queues,
  selectedQueueSlug,
  selectQueue,
  onCreateQueue,
}: {
  queue: NonNullable<RouterOutputs["queue"]["get"]>;
  form: ReturnType<typeof useQueueSettingsForm>;
  clubId: string;
  slug: string;
  section: SectionId;
  setSection: (id: SectionId) => void;
  queues: { id: string; slug: string; name: string; iconUrl: string | null; isEnabled: boolean }[];
  selectedQueueSlug: string | null;
  selectQueue: (slug: string) => void;
  onCreateQueue: () => void;
}) {
  const [helpSection, setHelpSection] = useState<SectionId | null>(null);

  const sectionRenderers: Record<SectionId, () => React.ReactNode> = {
    general: () => <GeneralSection form={form} clubId={clubId} clubGameId={queue.clubGameId} />,
    team: () => <TeamSection form={form} />,
    restrictions: () => (
      <FeatureGate clubId={clubId} slug={slug}>
        <RestrictionsSection form={form} />
      </FeatureGate>
    ),
    match: () => (
      <FeatureGate clubId={clubId} slug={slug}>
        <MatchSection form={form} />
      </FeatureGate>
    ),
    matchmaking: () => (
      <FeatureGate clubId={clubId} slug={slug}>
        <MatchmakingSection form={form} />
      </FeatureGate>
    ),
    ranks: () => <RanksSection form={form} />,
    danger: () => <DangerSection form={form} />,
  };

  return (
    <>
      {/* Queue tab bar */}
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-none -mx-4 px-4 md:mx-0 md:px-0 pb-1">
        {queues.map((q) => {
          const isSelected = q.slug === selectedQueueSlug;
          return (
            <button
              key={q.id}
              type="button"
              onClick={() => selectQueue(q.slug)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 shrink-0 ${
                isSelected
                  ? "bg-brand-500/10 text-brand-400 border border-brand-500/25"
                  : "bg-surface-800/50 text-surface-400 border border-surface-700/40 hover:text-surface-200 hover:border-surface-600/50"
              }`}
            >
              {q.iconUrl ? (
                <img src={q.iconUrl} alt="" className="w-5 h-5 rounded object-cover shrink-0" />
              ) : null}
              <span>{q.name}</span>
              {!q.isEnabled && (
                <span className="text-2xs font-bold text-amber-400/80 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded uppercase tracking-wider">
                  Off
                </span>
              )}
            </button>
          );
        })}
        <button
          type="button"
          onClick={onCreateQueue}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 shrink-0 text-brand-400 border border-dashed border-brand-500/25 hover:bg-brand-500/5 hover:border-brand-500/40"
        >
          <Plus className="size-3.5" />
          New
        </button>
      </div>

      {/* Mobile: nav strip */}
      <div className="lg:hidden">
        <div className="overflow-x-auto scrollbar-none -mx-4 px-4">
          <div className="flex gap-1 min-w-max pb-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = section === item.id;
              return (
                <React.Fragment key={item.id}>
                  {item.danger && (
                    <a
                      href={`/app/clubs/${slug}/queues/${queue.slug}/flow-editor`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200 text-surface-400 hover:text-surface-200 hover:bg-surface-800/50"
                    >
                      <Workflow className="size-3.5" />
                      Flow Editor
                      <ExternalLink className="size-3 text-surface-500" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => setSection(item.id)}
                    className={`relative flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
                      isActive
                        ? item.danger
                          ? "bg-error-500/10 text-error-400"
                          : "bg-brand-500/10 text-brand-400 shadow-brand-glow-sm"
                        : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/50"
                    }`}
                  >
                    <Icon className="size-3.5" />
                    {item.label}
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Desktop: sidebar + content */}
      <div className="flex gap-6">
        <nav className="hidden lg:block w-52 shrink-0 border-r border-surface-700/30">
          <div className="sticky top-6 space-y-1 pr-4">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = section === item.id;
              return (
                <React.Fragment key={item.id}>
                  {item.danger && (
                    <>
                      <a
                        href={`/app/clubs/${slug}/queues/${queue.slug}/flow-editor`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 text-surface-400 hover:text-surface-200 hover:bg-surface-800/40"
                      >
                        <div className="flex items-center justify-center size-8 rounded-lg transition-all duration-200 shrink-0 bg-surface-800/50 text-surface-500 group-hover:text-surface-300 group-hover:bg-surface-800">
                          <Workflow className="size-4" />
                        </div>
                        <span className="text-sm font-semibold">Flow Editor</span>
                        <ExternalLink className="size-3 text-surface-500 ml-auto" />
                      </a>
                      <div className="my-2 border-t border-separator" />
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => setSection(item.id)}
                    className={`group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                      isActive
                        ? item.danger
                          ? "bg-error-500/10 text-white"
                          : "bg-brand-500/8 text-white"
                        : item.danger
                          ? "text-error-400/60 hover:text-error-400 hover:bg-error-500/5"
                          : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/40"
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center size-8 rounded-lg transition-all duration-200 shrink-0 ${
                        isActive
                          ? item.danger
                            ? "bg-error-500/15 text-error-400"
                            : "bg-brand-500/15 text-brand-400"
                          : item.danger
                            ? "bg-error-500/10 text-error-400/60 group-hover:text-error-400"
                            : "bg-surface-800/50 text-surface-500 group-hover:text-surface-300 group-hover:bg-surface-800"
                      }`}
                    >
                      <Icon className="size-4" />
                    </div>
                    <span className={`text-sm font-semibold ${isActive ? "text-white" : ""}`}>
                      {item.label}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </nav>

        {/* Content panel */}
        <div className="flex-1 min-w-0">
          <SettingsSection
            title={sectionTitles[section]}
            icon={navItems.find((n) => n.id === section)?.icon ?? Settings}
            danger={section === "danger"}
            action={
              <button
                type="button"
                onClick={() => setHelpSection(section)}
                className={`p-1.5 rounded-md text-surface-500 transition-colors ${
                  section === "danger"
                    ? "hover:text-error-400 hover:bg-error-500/10"
                    : "hover:text-brand-400 hover:bg-surface-700/40"
                }`}
                aria-label={`Help for ${sectionTitles[section]}`}
              >
                <HelpCircle className="size-4" />
              </button>
            }
          >
            {sectionRenderers[section]()}
          </SettingsSection>
        </div>
      </div>

      {/* Documentation drawer */}
      <Drawer open={helpSection !== null} onClose={() => setHelpSection(null)}>
        <DrawerHeader subtitle={helpSection ? sectionTitles[helpSection] : undefined}>
          Documentation
        </DrawerHeader>
        <DrawerBody>{helpSection && sectionDocs[helpSection]}</DrawerBody>
      </Drawer>
    </>
  );
}
